/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBookOpen, FaCalculator, FaGraduationCap, FaList, FaLock, FaSearch, FaThLarge } from 'react-icons/fa';
import { MdDownload } from 'react-icons/md';
import { authContext } from '../Context/AuthContext';
import { globalContext } from '../Context/GlobalContext';
import { shouldShowMarca } from '../../utilities/marcasOcultas';
import { generarExcel } from '../../utilities/helpers/generadorExcel';
import TarjetaProducto from './TarjetaProducto';
import StoreLogin from './StoreLogin';
import { normalizar, paginasConPuntos, tienePrecio, tituloArticulo } from './storeUtils';
import styles from './Store.module.css';

const apiProductos = process.env.REACT_APP_API_PRODUCTOS;
const POR_PAGINA = 24;
const MARCAS_VISIBLES = 8;
const ORDENES = ['rel', 'nombre', 'menor', 'mayor'];

const comparar = (a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' });
const soloDigitos = (v) => v.replace(/\D/g, '');

export const Store = () => {
    const [t] = useTranslation('global');
    const { token } = useContext(authContext);
    const { colorTheme } = useContext(globalContext);
    const country = process.env.REACT_APP_COUNTRY;

    const [sp, setSp] = useSearchParams();
    const spStr = sp.toString();

    // ----- Datos: un unico GET a /info (sin cambios respecto del prototipo) -----
    const [articulos, setArticulos] = useState([]);
    const [load, setLoad] = useState(false);
    const [error, setError] = useState(false);
    const [reintento, setReintento] = useState(0);

    useEffect(() => {
        // La tienda es solo para clientes con sesion: sin token no se pide el catalogo
        if (!token) return undefined;

        let cancelado = false;
        setLoad(false);
        setError(false);

        axios
            .get(`${apiProductos}/info`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                timeout: 30000,
            })
            .then((res) => {
                if (cancelado) return;
                setArticulos(Array.isArray(res.data?.articulos) ? res.data.articulos : []);
            })
            .catch((err) => {
                if (cancelado) return;
                console.error('Store: error al cargar el catalogo', err);
                setError(true);
            })
            .finally(() => {
                if (!cancelado) setLoad(true);
            });

        return () => {
            cancelado = true;
        };
    }, [token, reintento]);

    // ----- Catalogo base -----
    const items = useMemo(
        () =>
            articulos
                .filter((art) => shouldShowMarca({ Descripcion: art.Marca }))
                .map((art) => ({
                    art,
                    titulo: tituloArticulo(art),
                    txt: normalizar(`${art.Alias} ${art.Nombre ?? ''} ${art.Parte_fabricante ?? ''} ${art.Marca ?? ''}`),
                })),
        [articulos]
    );

    const hayPrecios = useMemo(() => items.some((it) => tienePrecio(it.art)), [items]);
    const puedeFiltrarPrecio = !!token && hayPrecios;

    const todasLasMarcas = useMemo(
        () => [...new Set(items.map((it) => it.art.Marca).filter(Boolean))].sort(comparar),
        [items]
    );

    const todasLasGarantias = useMemo(
        () =>
            [...new Set(items.map((it) => Number(it.art.Garantia_meses)).filter((n) => n > 0))].sort((a, b) => a - b),
        [items]
    );

    // ----- Filtros: la URL es la unica fuente de verdad -----
    const filtros = useMemo(() => {
        const p = new URLSearchParams(spStr);
        const sort = p.get('sort');
        return {
            q: p.get('q') || '',
            marcas: p.getAll('marca'),
            gars: p.getAll('gar'),
            // "Solo con stock" viene activado por defecto; en la URL solo se guarda cuando se desactiva (stock=0)
            soloStock: puedeFiltrarPrecio && p.get('stock') !== '0',
            min: puedeFiltrarPrecio ? soloDigitos(p.get('min') || '') : '',
            max: puedeFiltrarPrecio ? soloDigitos(p.get('max') || '') : '',
            sort: ORDENES.includes(sort) && (hayPrecios || (sort !== 'menor' && sort !== 'mayor')) ? sort : 'rel',
            page: Math.max(1, parseInt(p.get('page'), 10) || 1),
        };
    }, [spStr, puedeFiltrarPrecio, hayPrecios]);

    const actualizar = (mutar, resetPagina = true) => {
        const next = new URLSearchParams(spStr);
        mutar(next);
        if (resetPagina) next.delete('page');
        if (next.toString() !== spStr) setSp(next, { replace: true });
    };

    const alternar = (clave, valor) =>
        actualizar((n) => {
            const actuales = n.getAll(clave);
            n.delete(clave);
            const nuevos = actuales.includes(valor) ? actuales.filter((v) => v !== valor) : [...actuales, valor];
            nuevos.forEach((v) => n.append(clave, v));
        });

    const fijar = (clave, valor) =>
        actualizar((n) => {
            if (valor === '' || valor === null || valor === false) n.delete(clave);
            else n.set(clave, valor);
        });

    // Input del buscador con estado local; se sincroniza con la URL solo si difiere
    const [qInput, setQInput] = useState(filtros.q);
    useEffect(() => {
        if (filtros.q !== qInput) setQInput(filtros.q);
    }, [filtros.q, qInput]);

    const cambiarBusqueda = (valor) => {
        setQInput(valor);
        fijar('q', valor);
    };

    // ----- Filtrado, facetas y orden (todo en el navegador) -----
    const { filtrados, conteoMarcas, conteoGar, conteoStock } = useMemo(() => {
        const tokens = normalizar(filtros.q).split(/\s+/).filter(Boolean);
        const min = filtros.min === '' ? null : Number(filtros.min);
        const max = filtros.max === '' ? null : Number(filtros.max);

        const cumple = (it, omitir) => {
            const art = it.art;
            if (tokens.length && !tokens.every((tk) => it.txt.includes(tk))) return false;
            if (omitir !== 'marca' && filtros.marcas.length && !filtros.marcas.includes(art.Marca)) return false;
            if (omitir !== 'gar' && filtros.gars.length && !filtros.gars.includes(String(Number(art.Garantia_meses)))) return false;
            if (omitir !== 'stock' && filtros.soloStock && !(Number(art.Stock) > 0)) return false;
            if (min !== null || max !== null) {
                if (!tienePrecio(art)) return false;
                const p = Number(art.Precio);
                if (min !== null && p < min) return false;
                if (max !== null && p > max) return false;
            }
            return true;
        };

        const cMarcas = {};
        const cGar = {};
        const res = [];
        let cStock = 0;
        items.forEach((it) => {
            if (Number(it.art.Stock) > 0 && cumple(it, 'stock')) cStock += 1;
            if (cumple(it, 'marca') && it.art.Marca) cMarcas[it.art.Marca] = (cMarcas[it.art.Marca] || 0) + 1;
            if (cumple(it, 'gar')) {
                const g = Number(it.art.Garantia_meses);
                if (g > 0) cGar[g] = (cGar[g] || 0) + 1;
            }
            if (cumple(it)) res.push(it);
        });

        const precioDe = (it) => (tienePrecio(it.art) ? Number(it.art.Precio) : null);
        if (filtros.sort === 'nombre') {
            res.sort((a, b) => comparar(a.titulo, b.titulo));
        } else if (filtros.sort === 'menor' || filtros.sort === 'mayor') {
            const dir = filtros.sort === 'menor' ? 1 : -1;
            res.sort((a, b) => {
                const pa = precioDe(a);
                const pb = precioDe(b);
                if (pa === null && pb === null) return 0;
                if (pa === null) return 1;
                if (pb === null) return -1;
                return (pa - pb) * dir;
            });
        } else if (tokens.length) {
            // Relevancia: primero los que empiezan con el termino buscado en SKU o parte
            const primero = tokens[0];
            const puntaje = (it) =>
                normalizar(it.art.Alias).startsWith(primero) || normalizar(it.art.Parte_fabricante ?? '').startsWith(primero) ? 0 : 1;
            res.sort((a, b) => puntaje(a) - puntaje(b));
        }

        return { filtrados: res, conteoMarcas: cMarcas, conteoGar: cGar, conteoStock: cStock };
    }, [items, filtros]);

    // ----- Paginado -----
    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
    const pagina = Math.min(filtros.page, totalPaginas);
    const desde = filtrados.length === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
    const hasta = Math.min(pagina * POR_PAGINA, filtrados.length);
    const paginaActual = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

    const resultadosRef = useRef(null);
    const irAPagina = (n) => {
        fijarPagina(n);
        if (resultadosRef.current) resultadosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const fijarPagina = (n) => actualizar((p) => (n <= 1 ? p.delete('page') : p.set('page', String(n))), false);

    // ----- UI local -----
    const [vista, setVista] = useState('grilla');
    const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
    const [busquedaMarca, setBusquedaMarca] = useState('');
    const [verTodasMarcas, setVerTodasMarcas] = useState(false);

    // El panel de filtros a pantalla completa solo existe en movil: bloquea el scroll mientras
    // esta abierto y se cierra solo si la ventana pasa a ancho de escritorio (o con Escape)
    useEffect(() => {
        if (!filtrosAbiertos) return undefined;

        const previo = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const mq = window.matchMedia('(max-width: 860px)');
        const alCambiar = (e) => {
            if (!e.matches) setFiltrosAbiertos(false);
        };
        const alTeclear = (e) => {
            if (e.key === 'Escape') setFiltrosAbiertos(false);
        };
        if (!mq.matches) setFiltrosAbiertos(false);
        if (mq.addEventListener) mq.addEventListener('change', alCambiar);
        else mq.addListener(alCambiar);
        document.addEventListener('keydown', alTeclear);

        return () => {
            document.body.style.overflow = previo;
            if (mq.removeEventListener) mq.removeEventListener('change', alCambiar);
            else mq.removeListener(alCambiar);
            document.removeEventListener('keydown', alTeclear);
        };
    }, [filtrosAbiertos]);

    // ----- Marcas del panel -----
    const marcasMostradas = useMemo(() => {
        const bq = normalizar(busquedaMarca.trim());
        return todasLasMarcas.filter((m) => {
            const sel = filtros.marcas.includes(m);
            if (!sel && !(conteoMarcas[m] > 0)) return false;
            return !bq || normalizar(m).includes(bq);
        });
    }, [todasLasMarcas, conteoMarcas, filtros.marcas, busquedaMarca]);

    const recorteActivo = !verTodasMarcas && busquedaMarca.trim() === '' && marcasMostradas.length > MARCAS_VISIBLES;
    const marcasLista = recorteActivo ? marcasMostradas.slice(0, MARCAS_VISIBLES) : marcasMostradas;

    const garantiasMostradas = todasLasGarantias.filter(
        (g) => conteoGar[g] > 1 || filtros.gars.includes(String(g))
    );

    // ----- Chips y contador de filtros activos -----
    const chips = [];
    if (filtros.q.trim()) chips.push({ id: 'q', texto: `“${filtros.q.trim()}”`, quitar: () => cambiarBusqueda('') });
    filtros.marcas.forEach((m) => chips.push({ id: `m-${m}`, texto: m, quitar: () => alternar('marca', m) }));
    // "Solo con stock" es el estado por defecto: no se lista como filtro activo (se ve y se cambia en su caja)
    if (filtros.min !== '' || filtros.max !== '') {
        const texto =
            filtros.min !== '' && filtros.max !== ''
                ? `${filtros.min} – ${filtros.max}`
                : filtros.min !== ''
                    ? `≥ ${filtros.min}`
                    : `≤ ${filtros.max}`;
        chips.push({
            id: 'precio',
            texto: `${t('store.precio')}: ${texto}`,
            quitar: () => actualizar((n) => { n.delete('min'); n.delete('max'); }),
        });
    }
    filtros.gars.forEach((g) =>
        chips.push({ id: `g-${g}`, texto: t('store.garantia-meses', { n: g }), quitar: () => alternar('gar', g) })
    );

    const limpiarTodo = () => {
        setQInput('');
        setBusquedaMarca('');
        actualizar((n) => {
            ['q', 'marca', 'gar', 'stock', 'min', 'max'].forEach((k) => n.delete(k));
        });
    };

    const fijarRango = (min, max) =>
        actualizar((n) => {
            if (min === '') n.delete('min'); else n.set('min', min);
            if (max === '') n.delete('max'); else n.set('max', max);
        });

    const rangoActivo = (min, max) => filtros.min === min && filtros.max === max;

    // ----- Excel con lo filtrado -----
    const descargarExcel = () => {
        const filas = filtrados.map(({ art, titulo }) => {
            const fila = {
                SKU: art.Alias,
                Nombre: titulo,
                Marca: art.Marca,
                'Garantía (meses)': art.Garantia_meses ?? '',
            };
            if (hayPrecios) {
                fila.Moneda = art.Moneda_Signo ?? '';
                fila.Precio = tienePrecio(art) ? art.Precio : '';
                fila.Stock = art.Stock ?? '';
            }
            return fila;
        });
        generarExcel(filas, () => { });
    };

    // ----- Estadisticas y textos -----
    const totalMarcas = todasLasMarcas.length;
    // Voseo en ARG, UY y PY; en el resto de las sucursales, un título neutro (formal)
    const tituloCliente = ['ARG', 'UY', 'PY'].includes(country)
        ? 'store.hero-titulo-cliente'
        : 'store.hero-titulo-cliente-neutro';
    const mostrarCapacitaciones = country !== 'JM' && country !== 'TT' && country !== 'PR';
    const notaImpuestos = hayPrecios && country !== 'USA' ? (country === 'PA' ? t('store.sin-itbms') : t('store.sin-iva')) : null;
    const unaMarca = filtros.marcas.length === 1 ? filtros.marcas[0] : null;

    const bloqueado = (titulo) => (
        <div className={styles.bloqueado}>
            <FaLock aria-hidden="true" />
            <div>
                <strong>{titulo}</strong>
                <div>{t('store.bloqueado-texto')}</div>
            </div>
        </div>
    );

    if (!token) return <StoreLogin />;

    return (
        <div className={styles.root} data-theme={colorTheme === 'dark' ? 'dark' : 'light'}>
            {/* ===== Bienvenida ===== */}
            <div className={styles.hero}>
                <div className={`${styles.wrap} ${styles.heroTop}`}>
                    <div>
                        <div className={styles.kicker}>{t('store.bienvenida-kicker')}</div>
                        <h1>{token ? t(tituloCliente) : t('store.hero-titulo-visitante')}</h1>
                        <p>{token ? t('store.hero-texto-cliente') : t('store.hero-texto-visitante')}</p>
                    </div>
                    <div className={styles.stats}>
                        <div className={styles.stat}>
                            <b>{load && !error ? items.length.toLocaleString('es-AR') : '–'}</b>
                            <span>{t('store.stat-productos')}</span>
                        </div>
                        <div className={styles.stat}>
                            <b>{load && !error ? totalMarcas.toLocaleString('es-AR') : '–'}</b>
                            <span>{t('store.stat-marcas')}</span>
                        </div>
                    </div>
                </div>
                <div className={styles.atajos}>
                    <div className={`${styles.wrap} ${styles.atajosFila}`}>
                        <Link to="/selectorcatalogo" className={styles.atajo}>
                            <FaBookOpen aria-hidden="true" /> {t('store.atajo-catalogo')}
                        </Link>
                        {mostrarCapacitaciones && (
                            <Link to="/capacitaciones" className={styles.atajo}>
                                <FaGraduationCap aria-hidden="true" /> {t('store.atajo-capacitaciones')}
                            </Link>
                        )}
                        <Link to="/Iquote" className={styles.atajo}>
                            <FaCalculator aria-hidden="true" /> {t('store.atajo-iquote')}
                        </Link>
                    </div>
                </div>
            </div>

            {/* ===== Buscador ===== */}
            <div className={styles.buscadorBanda}>
                <div className={styles.wrap}>
                    <form className={styles.buscadorForm} onSubmit={(e) => e.preventDefault()} role="search">
                        <div className={styles.buscador}>
                            <FaSearch className={styles.buscadorIcono} aria-hidden="true" />
                            <input
                                type="search"
                                value={qInput}
                                onChange={(e) => cambiarBusqueda(e.target.value)}
                                placeholder={t('store.buscar')}
                                aria-label={t('store.buscar')}
                            />
                            <button type="submit">{t('store.buscar-btn')}</button>
                        </div>
                    </form>
                </div>
            </div>

            <div className={styles.wrap}>

                {/* ===== Cuerpo ===== */}
                <div className={styles.cuerpoGrid}>
                    {/* ----- Filtros ----- */}
                    <aside className={`${styles.filtros} ${filtrosAbiertos ? styles.filtrosAbiertos : ''}`} aria-label={t('store.filtros')}>
                        <button type="button" className={styles.cerrarFiltros} onClick={() => setFiltrosAbiertos(false)}>
                            {t('store.cerrar')} ✕
                        </button>

                        <div className={styles.filtrosCab}>
                            <strong>{t('store.filtros')}</strong>
                            {chips.length > 0 && (
                                <button type="button" className={styles.linkRojo} onClick={limpiarTodo}>
                                    {t('store.limpiar-todo')}
                                </button>
                            )}
                        </div>

                        {/* Disponibilidad: primer grupo y destacado */}
                        {!token && (
                            <div className={styles.grupoDestacado}>
                                <h2>{t('store.disponibilidad')}</h2>
                                {bloqueado(t('store.stock-disponible'))}
                            </div>
                        )}
                        {puedeFiltrarPrecio && (
                            <div className={`${styles.grupoDestacado} ${filtros.soloStock ? styles.grupoDestacadoOn : ''}`}>
                                <h2>{t('store.disponibilidad')}</h2>
                                <label className={styles.switchFila}>
                                    <span className={styles.switchTexto}>
                                        {t('store.solo-stock')}
                                        <small>{t('store.stock-n-productos', { n: conteoStock.toLocaleString('es-AR') })}</small>
                                    </span>
                                    <input
                                        type="checkbox"
                                        className={styles.switch}
                                        checked={filtros.soloStock}
                                        onChange={(e) => fijar('stock', e.target.checked ? '' : '0')}
                                    />
                                </label>
                            </div>
                        )}

                        {/* Marca */}
                        <div className={styles.grupo}>
                            <h2>{t('store.marcas')}</h2>
                            <input
                                type="text"
                                className={styles.inputChico}
                                value={busquedaMarca}
                                onChange={(e) => setBusquedaMarca(e.target.value)}
                                placeholder={t('store.buscar-marca')}
                                aria-label={t('store.buscar-marca')}
                            />
                            <ul className={styles.listaMarcas}>
                                {marcasLista.map((m) => {
                                    const cant = conteoMarcas[m] || 0;
                                    return (
                                        <li key={m} className={cant === 0 ? styles.atenuada : ''}>
                                            <label className={styles.casilla}>
                                                <input
                                                    type="checkbox"
                                                    checked={filtros.marcas.includes(m)}
                                                    onChange={() => alternar('marca', m)}
                                                />
                                                <span className={styles.casillaTxt}>{m}</span>
                                                <span className={styles.cant}>{cant}</span>
                                            </label>
                                        </li>
                                    );
                                })}
                                {marcasLista.length === 0 && <li className={styles.cant}>{t('store.sin-marcas')}</li>}
                            </ul>
                            {marcasMostradas.length > MARCAS_VISIBLES && busquedaMarca.trim() === '' && (
                                <button type="button" className={styles.linkRojo} onClick={() => setVerTodasMarcas((v) => !v)}>
                                    {verTodasMarcas ? t('store.ver-menos') : t('store.ver-todas-marcas', { n: marcasMostradas.length })}
                                </button>
                            )}
                        </div>

                        {/* Precio */}
                        {!token && (
                            <div className={styles.grupo}>
                                <h2>{t('store.precio')}</h2>
                                {bloqueado(t('store.rango-precio'))}
                            </div>
                        )}
                        {puedeFiltrarPrecio && (
                            <div className={styles.grupo}>
                                <h2>{t('store.precio')}</h2>
                                <div className={styles.precioInputs}>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={styles.inputChico}
                                        placeholder={t('store.precio-desde')}
                                        aria-label={t('store.precio-desde')}
                                        value={filtros.min}
                                        onChange={(e) => fijar('min', soloDigitos(e.target.value))}
                                    />
                                    <span>-</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={styles.inputChico}
                                        placeholder={t('store.precio-hasta')}
                                        aria-label={t('store.precio-hasta')}
                                        value={filtros.max}
                                        onChange={(e) => fijar('max', soloDigitos(e.target.value))}
                                    />
                                </div>
                                <div className={styles.pildoras}>
                                    <button type="button" className={`${styles.pildora} ${rangoActivo('', '50') ? styles.pildoraOn : ''}`} onClick={() => fijarRango('', '50')}>
                                        {t('store.precio-hasta-50')}
                                    </button>
                                    <button type="button" className={`${styles.pildora} ${rangoActivo('50', '200') ? styles.pildoraOn : ''}`} onClick={() => fijarRango('50', '200')}>
                                        {t('store.precio-50-200')}
                                    </button>
                                    <button type="button" className={`${styles.pildora} ${rangoActivo('200', '') ? styles.pildoraOn : ''}`} onClick={() => fijarRango('200', '')}>
                                        {t('store.precio-mas-200')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Garantia */}
                        {garantiasMostradas.length > 0 && (
                            <div className={styles.grupo}>
                                <h2>{t('store.garantia')}</h2>
                                <ul className={styles.listaMarcas}>
                                    {garantiasMostradas.map((g) => {
                                        const cant = conteoGar[g] || 0;
                                        return (
                                            <li key={g} className={cant === 0 ? styles.atenuada : ''}>
                                                <label className={styles.casilla}>
                                                    <input
                                                        type="checkbox"
                                                        checked={filtros.gars.includes(String(g))}
                                                        onChange={() => alternar('gar', String(g))}
                                                    />
                                                    <span className={styles.casillaTxt}>{t('store.garantia-meses', { n: g })}</span>
                                                    <span className={styles.cant}>{cant}</span>
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        <div className={styles.verResultados}>
                            <button type="button" className={`${styles.btn} ${styles.btnRojo}`} onClick={() => setFiltrosAbiertos(false)}>
                                {t('store.ver-resultados')}
                            </button>
                        </div>
                    </aside>

                    {/* ----- Resultados ----- */}
                    <div className={styles.resultados} ref={resultadosRef}>
                        <nav className={styles.migas} aria-label="breadcrumb">
                            <Link to="/">{t('store.inicio')}</Link> › {unaMarca ? <Link to="/store">{t('store.titulo')}</Link> : <b>{t('store.titulo')}</b>}
                            {unaMarca && <> › <b>{unaMarca}</b></>}
                        </nav>

                        <div className={styles.cabResultados}>
                            <h2 aria-live="polite">
                                {load && !error
                                    ? filtrados.length === 1
                                        ? t('store.resultado-uno')
                                        : t('store.resultados-n', { n: filtrados.length.toLocaleString('es-AR') })
                                    : t('store.cargando')}
                            </h2>
                            <div className={styles.herramientas}>
                                <button type="button" className={`${styles.btn} ${styles.btnGhost} ${styles.btnFiltrosMobile}`} onClick={() => setFiltrosAbiertos(true)}>
                                    {t('store.filtros-n', { n: chips.length })}
                                </button>
                                <select
                                    className={styles.select}
                                    value={filtros.sort}
                                    onChange={(e) => fijar('sort', e.target.value === 'rel' ? '' : e.target.value)}
                                    aria-label={t('store.orden')}
                                >
                                    <option value="rel">{t('store.orden-relevantes')}</option>
                                    <option value="nombre">{t('store.orden-nombre')}</option>
                                    {hayPrecios && <option value="menor">{t('store.orden-menor')}</option>}
                                    {hayPrecios && <option value="mayor">{t('store.orden-mayor')}</option>}
                                </select>
                                <div className={styles.vistaGrupo} role="group" aria-label={t('store.vista')}>
                                    <button
                                        type="button"
                                        className={vista === 'grilla' ? styles.vistaOn : ''}
                                        onClick={() => setVista('grilla')}
                                        aria-label={t('store.vista-cuadricula')}
                                        aria-pressed={vista === 'grilla'}
                                    >
                                        <FaThLarge aria-hidden="true" />
                                    </button>
                                    <button
                                        type="button"
                                        className={vista === 'lista' ? styles.vistaOn : ''}
                                        onClick={() => setVista('lista')}
                                        aria-label={t('store.vista-lista')}
                                        aria-pressed={vista === 'lista'}
                                    >
                                        <FaList aria-hidden="true" />
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    className={`${styles.btn} ${styles.btnGhost}`}
                                    onClick={descargarExcel}
                                    disabled={!load || error || filtrados.length === 0}
                                >
                                    <MdDownload size={16} aria-hidden="true" /> {t('store.excel')}
                                </button>
                            </div>
                        </div>

                        {chips.length > 0 && (
                            <div className={styles.chips}>
                                {chips.map((c) => (
                                    <span key={c.id} className={styles.chip}>
                                        {c.texto}
                                        <button type="button" onClick={c.quitar} aria-label={`${t('store.quitar')}: ${c.texto}`}>
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {notaImpuestos && <p className={styles.nota}>{notaImpuestos}</p>}

                        {!load && (
                            <div className={styles.grilla} aria-busy="true">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div key={i} className={styles.esqueleto} />
                                ))}
                            </div>
                        )}

                        {load && error && (
                            <div className={styles.estado}>
                                <h3>{t('store.error')}</h3>
                                <button type="button" className={`${styles.btn} ${styles.btnRojo}`} onClick={() => setReintento((n) => n + 1)}>
                                    {t('store.reintentar')}
                                </button>
                            </div>
                        )}

                        {load && !error && filtrados.length === 0 && (
                            <div className={`${styles.estado} ${styles.estadoVacio}`}>
                                <h3>{t('store.vacio-titulo')}</h3>
                                <p>{t('store.vacio-texto')}</p>
                                {chips.length > 0 && (
                                    <button type="button" className={`${styles.btn} ${styles.btnRojo}`} onClick={limpiarTodo}>
                                        {t('store.limpiar-todo')}
                                    </button>
                                )}
                            </div>
                        )}

                        {load && !error && filtrados.length > 0 && (
                            <>
                                <div className={vista === 'lista' ? styles.lista : styles.grilla}>
                                    {paginaActual.map(({ art }) => (
                                        <TarjetaProducto key={art.Alias} articulo={art} vista={vista} token={token} stockReal={puedeFiltrarPrecio} />
                                    ))}
                                </div>

                                <div className={styles.paginado}>
                                    <span className={styles.mostrando}>
                                        {t('store.mostrando', {
                                            desde: desde.toLocaleString('es-AR'),
                                            hasta: hasta.toLocaleString('es-AR'),
                                            total: filtrados.length.toLocaleString('es-AR'),
                                        })}
                                    </span>
                                    {totalPaginas > 1 && (
                                        <nav className={styles.paginas} aria-label="paginación">
                                            <button type="button" disabled={pagina === 1} onClick={() => irAPagina(pagina - 1)} aria-label={t('store.pagina-anterior')}>
                                                ‹
                                            </button>
                                            {paginasConPuntos(pagina, totalPaginas).map((n) =>
                                                typeof n === 'string' ? (
                                                    <span key={n} className={styles.puntos}>…</span>
                                                ) : (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        className={n === pagina ? styles.paginaOn : ''}
                                                        aria-current={n === pagina ? 'page' : undefined}
                                                        onClick={() => irAPagina(n)}
                                                    >
                                                        {n}
                                                    </button>
                                                )
                                            )}
                                            <button type="button" disabled={pagina === totalPaginas} onClick={() => irAPagina(pagina + 1)} aria-label={t('store.pagina-siguiente')}>
                                                ›
                                            </button>
                                        </nav>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Store;
